/**
 * The virtual-background id space.
 *
 * The picker's options are mutually exclusive, so they share one id: `''` (no background), `'blur'`,
 * or `custom:<uuid>` for an image the user picked from their own device (held in this browser only —
 * see `webRTC/services/customBackgroundStorage`).
 *
 * A prefix on the id rather than a second field, because the whole pipeline — form field →
 * `settingsUpdated` → `userSlice` → `localStorage` → apply — already carries exactly one string, and
 * `isCustomBackgroundId` is synchronous, so it can be used even at slice-init time.
 *
 * This used to hold a second tenant: `VIDEO_BACKGROUNDS`, a hard-coded list of bundled artwork gated
 * to one client's accounts. That pilot is over, and removing it also removes the seam it documented
 * for backend-served backgrounds. Deliberate rather than lost: the seam was one array of
 * `{ id, name, imageUrl }` plus a lookup, and `applyBackground` still takes a URL rather than an id,
 * so it can be reinstated exactly the way it was removed.
 */
import { uuid } from 'common/utils';

import type { BlurLevel } from '../types';

export const NO_BACKGROUND_ID = '';
export const BLUR_BACKGROUND_ID = 'blur';

export const CUSTOM_BACKGROUND_ID_PREFIX = 'custom:';

export const isCustomBackgroundId = (id: string) => id.startsWith(CUSTOM_BACKGROUND_ID_PREFIX);

export const createCustomBackgroundId = () => `${CUSTOM_BACKGROUND_ID_PREFIX}${uuid()}`;

/**
 * What "blur on" means, everywhere. Agora's degrees are `1` low, `2` medium, `3` high; the product uses
 * low, so a self-preview shows what the call actually publishes. Change it here and every surface moves
 * together — which is the point, because all of them write it to the same stored `blurLevel`.
 */
export const BLUR_LEVEL_ON: BlurLevel = 1;

/**
 * Which of the three mutually exclusive options an id names. Coarser than the id on purpose: telemetry
 * wants to know that an image was chosen without carrying which one.
 */
export type VideoBackgroundKind = 'none' | 'blur' | 'image';

export const videoBackgroundKind = (id: string): VideoBackgroundKind => {
  if (id === BLUR_BACKGROUND_ID) return 'blur';
  return isCustomBackgroundId(id) ? 'image' : 'none';
};

/**
 * The one-id ⇄ two-field translation, shared by every picker.
 *
 * The options are mutually exclusive, so the UI presents them as a single id — but they're *stored* as
 * `isBlurEnabled` + `backgroundImageId` (a form pair in the media settings, a Redux pair in
 * `user/state`). Keeping the mapping in one place is what stops the two fields contradicting each other
 * differently on each surface.
 */
export const toVideoBackgroundId = ({
  isBlurEnabled,
  backgroundImageId,
}: {
  isBlurEnabled: boolean;
  backgroundImageId: string;
}): string => backgroundImageId || (isBlurEnabled ? BLUR_BACKGROUND_ID : NO_BACKGROUND_ID);

export const fromVideoBackgroundId = (id: string): { isBlurEnabled: boolean; backgroundImageId: string } => ({
  isBlurEnabled: id === BLUR_BACKGROUND_ID,
  backgroundImageId: id === BLUR_BACKGROUND_ID ? NO_BACKGROUND_ID : id,
});
